import type { ConfirmationResult, RecaptchaVerifier, User } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { indianStateNames, pricing, webApp } from '../data/site';
import {
  fieldValue,
  firebaseMessage,
  formatDate,
  loadAuth,
  postJson,
  RequestTimeoutError,
  signOutPhone,
} from './accountForms';
import { track } from './analytics';
import { firebaseApp } from './firebase';
import { shareApiBase } from './share/shareContract';
import { fieldProblem } from './trialFormFields';
import {
  readSignupAnswer,
  readStatusAnswer,
  waitForTrial,
  type TrialOutcome,
  type TrialResult,
} from './trialSignupOutcome';

/**
 * The website's self-serve free trial. Everything about the organisation is typed here, the mobile
 * number is proven with a Firebase phone OTP, and the server (`TRIAL_SIGNUP_PATH` in
 * `@transport/contracts/contracts/organization`, under the same API base the shared statements
 * read) creates the organisation and its administrator at once — nobody approves it.
 *
 * Three steps on one form: the details, the code sent to the phone, then the answer. The details
 * stay in the form while the code is asked for, only hidden, so nothing has to be typed twice. Once
 * the number is verified it stays verified for the few minutes the server accepts the token, so a
 * taken username or a mistyped GSTIN is corrected and sent again without a second SMS.
 *
 * The create runs inside the request — a Turso database is made and the org seeded — so it is
 * given 90 seconds (inside Cloudflare's 100). Losing the answer is never read as a failure: the
 * form asks the server's status route, keyed on the same verified number, until the organisation
 * is ready or the server says nothing was made, and otherwise offers "Check again". Submitting
 * again is safe too: the server answers a number that is already set up with its username, and
 * one still being made with "pending" (`trialSignupOutcome.ts`).
 */

const TRIAL_SIGNUP_URL_PATH = '/public/trial-signup';
const TRIAL_STATUS_URL_PATH = '/public/trial-signup/status';
const TRIAL_SIGNUP_TIMEOUT_MS = 90_000;
const STATUS_TIMEOUT_MS = 10_000;
/** Ask every 5 s for two minutes before handing the decision to a "Check again" button. */
const STATUS_INTERVAL_MS = 5_000;
const STATUS_ATTEMPTS = 24;
const FLEET_SIZES = ['1–10', '11–50', '51–200', '201–500', '500+'];

type Step = 'details' | 'code' | 'verified' | 'check' | 'done';
type Status = { text: string; state: 'success' | 'error' | '' };

const CHECKING_MESSAGE =
  'This is taking longer than usual. Your organisation may still be being set up — we are checking, please keep this page open.';
const UNKNOWN_MESSAGE =
  'We still cannot tell whether your organisation is ready. Press “Check again” in a minute, or look for our welcome email. You do not need to fill the form in again.';
const NOTHING_MADE_MESSAGE =
  'Your organisation was not set up and nothing was kept. Your number is still verified — press “Start my free trial” to try again.';

const PROGRESS = ['Your details', 'Verify mobile', 'Start using it'];

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export function TrialSignupForm() {
  const form = useRef<HTMLFormElement>(null);
  const verifier = useRef<RecaptchaVerifier | null>(null);
  const confirmation = useRef<ConfirmationResult | null>(null);
  const verifiedUser = useRef<User | null>(null);
  const started = useRef(false);
  const [step, setStep] = useState<Step>('details');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>({ text: '', state: '' });
  const [result, setResult] = useState<TrialResult | null>(null);
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const statusBox = useRef<HTMLDivElement>(null);
  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'code') codeInput.current?.focus();
  }, [step]);

  // A long form can leave the answer below the fold: bring it into view when something goes wrong.
  useEffect(() => {
    if (status.state === 'error') statusBox.current?.scrollIntoView({ block: 'nearest' });
  }, [status]);

  /** Marks every field the browser finds invalid and moves to the first. Disabled ones are skipped. */
  function validate(element: HTMLFormElement): boolean {
    const found: Record<string, string> = {};
    let first: HTMLInputElement | null = null;
    for (const control of Array.from(element.elements) as HTMLInputElement[]) {
      if (!control.name || !control.willValidate) continue;
      const problem = fieldProblem(control.name, control.validity);
      if (!problem) continue;
      found[control.name] = problem;
      first ??= control;
    }
    setErrors(found);
    first?.focus();
    return !first;
  }

  /** Corrects a field as it is typed, and takes its message away once the person is on it. */
  function changed(event: React.FormEvent<HTMLFormElement>) {
    const control = event.target as HTMLInputElement;
    if (control.name === 'mobile' || control.name === 'code') {
      control.value = control.value.replace(/\D/g, '');
    } else if (control.name === 'gstin') {
      control.value = control.value.toUpperCase();
    }
    setErrors(current => {
      if (!current[control.name]) return current;
      const rest = { ...current };
      delete rest[control.name];
      return rest;
    });
  }

  const fail = (text: string) => {
    setStatus({ text, state: 'error' });
    track('trial_error', { step });
  };

  async function sendCode() {
    const element = form.current;
    if (!element || !validate(element)) return;
    const app = firebaseApp();
    if (!app || !shareApiBase()) {
      fail('Free trials are not available on this site yet. Please request a demo instead.');
      return;
    }
    const mobile = fieldValue(element, 'mobile').replace(/\D/g, '');
    setBusy(true);
    setStatus({ text: 'Sending a code to your phone…', state: '' });
    try {
      const { getAuth, RecaptchaVerifier, signInWithPhoneNumber } = await loadAuth();
      const auth = getAuth(app);
      verifier.current ??= new RecaptchaVerifier(auth, 'trial-recaptcha', { size: 'invisible' });
      confirmation.current = await signInWithPhoneNumber(auth, `+91${mobile}`, verifier.current);
      setSentTo(`+91 ${mobile}`);
      setStep('code');
      setStatus({ text: '', state: '' });
      track('trial_code_sent');
    } catch (error) {
      // A used or failed reCAPTCHA cannot be reused; the next attempt makes a fresh one.
      verifier.current?.clear();
      verifier.current = null;
      fail(firebaseMessage(error, 'We could not send a code. Check the number and try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode() {
    const code = fieldValue(form.current!, 'code').trim();
    if (!/^\d{6}$/.test(code)) {
      fail('Enter the 6-digit code from the SMS.');
      return;
    }
    setBusy(true);
    setStatus({ text: 'Checking your code…', state: '' });
    try {
      const credential = await confirmation.current!.confirm(code);
      verifiedUser.current = credential.user;
      setStep('verified');
      await startTrial();
    } catch (error) {
      fail(firebaseMessage(error, 'We could not verify that code. Try again.'));
      setBusy(false);
    }
  }

  async function startTrial() {
    const element = form.current;
    const user = verifiedUser.current;
    if (!element || !user || !validate(element)) return;
    setBusy(true);
    setStatus({ text: 'Setting up your organisation — this takes a few seconds…', state: '' });
    try {
      const value = (name: string) => fieldValue(element, name);
      const response = await postJson(
        `${shareApiBase()}${TRIAL_SIGNUP_URL_PATH}`,
        {
          firebaseIdToken: await user.getIdToken(),
          businessName: value('businessName'),
          contactName: value('contactName'),
          email: value('email'),
          gstin: value('gstin'),
          city: value('city'),
          state: value('state'),
          fleetSize: value('fleetSize'),
          username: value('username'),
          password: value('password'),
          website: value('website'),
        },
        TRIAL_SIGNUP_TIMEOUT_MS,
      );
      const body = await response.json().catch(() => ({}));
      setEmail(value('email').trim());
      const outcome = readSignupAnswer(response.status, body);
      if (outcome.kind === 'pending') await checkStatus(outcome);
      else await settle(outcome);
    } catch (error) {
      if (error instanceof RequestTimeoutError || error instanceof TypeError) {
        // Aborted by our timeout, or the network failed under us: ask, never guess.
        await checkStatus();
      } else {
        fail(error instanceof Error ? error.message : 'We could not start your trial.');
      }
    } finally {
      setBusy(false);
    }
  }

  /** Polls the status route until the answer is known, or hands over to "Check again". */
  async function checkStatus(pending?: TrialOutcome) {
    const user = verifiedUser.current;
    if (!user) return;
    setBusy(true);
    setStatus({
      text: pending
        ? 'Your organisation is still being set up from your last attempt — checking…'
        : CHECKING_MESSAGE,
      state: '',
    });
    try {
      const outcome = await waitForTrial({
        check: async () => {
          const response = await postJson(
            `${shareApiBase()}${TRIAL_STATUS_URL_PATH}`,
            { firebaseIdToken: await user.getIdToken() },
            STATUS_TIMEOUT_MS,
          );
          return readStatusAnswer(response.status, await response.json().catch(() => ({})));
        },
        sleep,
        attempts: STATUS_ATTEMPTS,
        intervalMs: STATUS_INTERVAL_MS,
      });
      if (outcome.kind === 'unknown') {
        setStep('check');
        fail(UNKNOWN_MESSAGE);
      } else if (outcome.kind === 'none') {
        setStep('verified');
        fail(NOTHING_MADE_MESSAGE);
      } else {
        await settle(outcome);
      }
    } finally {
      setBusy(false);
    }
  }

  async function settle(outcome: TrialOutcome) {
    if (outcome.kind === 'done') {
      setResult(outcome.result);
      setStep('done');
      setStatus({ text: '', state: '' });
      track('trial_submit');
      void signOutPhone();
    } else if (outcome.kind === 'reverify') {
      // The verification is too old for the server: back to a fresh code.
      await resetVerification();
      fail(outcome.message);
    } else if (outcome.kind === 'error') {
      fail(outcome.message);
    }
  }

  async function resetVerification() {
    confirmation.current = null;
    verifiedUser.current = null;
    verifier.current?.clear();
    verifier.current = null;
    setStep('details');
    setStatus({ text: '', state: '' });
    await signOutPhone();
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (step === 'details') void sendCode();
    else if (step === 'code') void verifyCode();
    else if (step === 'verified') void startTrial();
    else if (step === 'check') void checkStatus();
  }

  if (step === 'done' && result?.alreadySetUp) {
    return (
      <div className="card trial-done" role="status">
        <div className="eyebrow">Your trial is already set up</div>
        <h2>{result.name} is ready.</h2>
        <p>
          It was set up from your earlier attempt, so nothing new was created. Sign in on any
          Transport Book app with the username <strong>{result.username}</strong> and the password
          you chose then. Your free trial runs until{' '}
          <strong>{formatDate(result.trialEndsAt)}</strong>.
        </p>
        <p>
          Forgotten the password? <Link to="/reset-password">Reset it</Link> with a code sent to
          your mobile.
        </p>
        <div className="actions">
          <a
            className="button primary"
            href={webApp.url}
            rel="noopener"
            onClick={() => track('open_web_app', { placement: 'trial_done' })}
          >
            Open in the browser <span aria-hidden="true">↗</span>
          </a>
          <Link className="button secondary" to="/downloads/desktop">
            Download for desktop
          </Link>
          <Link className="button secondary" to="/downloads/android">
            Get the Android app
          </Link>
        </div>
      </div>
    );
  }

  if (step === 'done' && result) {
    return (
      <div className="card trial-done" role="status">
        <div className="eyebrow">Your trial is ready</div>
        <h2>{result.name} is set up.</h2>
        <p>
          Sign in on any Transport Book app with the username <strong>{result.username}</strong> and
          the password you chose. Your free trial runs until{' '}
          <strong>{formatDate(result.trialEndsAt)}</strong>.
        </p>
        <p>
          {result.welcomeEmailSent ? (
            <>
              We have emailed these details and your sign-in links to <strong>{email}</strong> —
              keep it for when you set up a second device.
            </>
          ) : (
            <>
              Write the username down: we could not send the welcome email just now. If you ever
              forget the password, <Link to="/reset-password">reset it</Link> with a code sent to
              your mobile.
            </>
          )}
        </p>
        <div className="actions">
          <a
            className="button primary"
            href={webApp.url}
            rel="noopener"
            onClick={() => track('open_web_app', { placement: 'trial_done' })}
          >
            Open in the browser <span aria-hidden="true">↗</span>
          </a>
          <Link className="button secondary" to="/downloads/desktop">
            Download for desktop
          </Link>
          <Link className="button secondary" to="/downloads/android">
            Get the Android app
          </Link>
        </div>
      </div>
    );
  }

  const detailsHidden = step === 'code' || step === 'check';
  const phoneLocked = step !== 'details';
  const progress = step === 'details' ? 0 : step === 'code' ? 1 : 2;
  const submitLabel = {
    details: busy ? 'Sending the code…' : 'Send verification code',
    code: busy ? 'Verifying…' : 'Verify and start my trial',
    check: busy ? 'Checking…' : 'Check again',
    verified: busy ? 'Setting up your organisation…' : 'Start my free trial',
    done: '',
  }[step];
  const message = (name: string) => errors[name];

  return (
    <form
      ref={form}
      className="card demo-form trial-form"
      onSubmit={submit}
      onInput={changed}
      onFocus={() => {
        if (started.current) return;
        started.current = true;
        track('trial_start');
      }}
      noValidate
    >
      <ol className="trial-progress">
        {PROGRESS.map((label, index) => (
          <li
            aria-current={index === progress ? 'step' : undefined}
            data-done={index < progress ? 'true' : undefined}
            key={label}
          >
            <span aria-hidden="true">{index < progress ? '✓' : index + 1}</span>
            {label}
          </li>
        ))}
      </ol>

      <fieldset disabled={detailsHidden || busy} hidden={detailsHidden}>
        <legend>Your business</legend>
        <div className="form-grid">
          <Field
            autoComplete="organization"
            error={message('businessName')}
            full
            label="Business name"
            name="businessName"
            placeholder="Sharma Roadways"
            required
          />
          <Field
            autoComplete="address-level2"
            error={message('city')}
            label="City"
            name="city"
            placeholder="Jaipur"
            required
          />
          <div className="field">
            <label htmlFor="state">State</label>
            <select
              aria-describedby={message('state') ? 'state-error' : undefined}
              aria-invalid={message('state') ? true : undefined}
              autoComplete="address-level1"
              defaultValue=""
              id="state"
              name="state"
              required
            >
              <option disabled value="">
                Select your state
              </option>
              {indianStateNames.map(name => (
                <option key={name}>{name}</option>
              ))}
            </select>
            <FieldError message={message('state')} name="state" />
          </div>
          <div className="field">
            <label htmlFor="fleetSize">
              Number of vehicles <span className="field-optional">Optional</span>
            </label>
            <select defaultValue="" id="fleetSize" name="fleetSize">
              <option value="">Select a rough size</option>
              {FLEET_SIZES.map(size => (
                <option key={size}>{size}</option>
              ))}
            </select>
          </div>
          <Field
            error={message('gstin')}
            hint="Leave empty if you are not registered."
            label="GSTIN"
            maxLength={15}
            name="gstin"
            optional
            pattern="[0-9A-Za-z]{15}"
            placeholder="08ABCDE1234F1Z5"
          />
        </div>
      </fieldset>

      <fieldset disabled={detailsHidden || busy} hidden={detailsHidden}>
        <legend>Your contact details</legend>
        <div className="form-grid">
          <Field
            autoComplete="name"
            error={message('contactName')}
            full
            label="Your name"
            name="contactName"
            required
          />
          <div className="field">
            <label htmlFor="mobile">Mobile number</label>
            <div className="phone-input">
              <span aria-hidden="true">+91</span>
              <input
                aria-describedby={message('mobile') ? 'mobile-error' : 'mobile-hint'}
                aria-invalid={message('mobile') ? true : undefined}
                autoComplete="tel-national"
                id="mobile"
                inputMode="numeric"
                maxLength={10}
                name="mobile"
                pattern="[6-9][0-9]{9}"
                placeholder="98765 43210"
                readOnly={phoneLocked}
                required
                type="tel"
              />
            </div>
            {message('mobile') ? null : (
              <small id="mobile-hint">We send a one-time code here by SMS.</small>
            )}
            <FieldError message={message('mobile')} name="mobile" />
          </div>
          <Field
            autoComplete="email"
            error={message('email')}
            hint="Your sign-in details are sent here."
            label="Email"
            name="email"
            placeholder="you@example.com"
            required
            type="email"
          />
        </div>
      </fieldset>

      <fieldset disabled={detailsHidden || busy} hidden={detailsHidden}>
        <legend>Your sign-in</legend>
        <div className="form-grid">
          <Field
            autoCapitalize="none"
            autoComplete="username"
            error={message('username')}
            hint="Letters, digits, dots or dashes, with no spaces."
            label="Username"
            name="username"
            pattern="[A-Za-z0-9][A-Za-z0-9._@\-]{2,59}"
            required
            spellCheck={false}
          />
          <div className="field">
            <label htmlFor="password">Password</label>
            <div className="password-input">
              <input
                aria-describedby={message('password') ? 'password-error' : 'password-hint'}
                aria-invalid={message('password') ? true : undefined}
                autoComplete="new-password"
                id="password"
                maxLength={120}
                minLength={8}
                name="password"
                required
                type={showPassword ? 'text' : 'password'}
              />
              <button
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                className="link-button"
                type="button"
                onClick={() => setShowPassword(shown => !shown)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {message('password') ? null : <small id="password-hint">At least 8 characters.</small>}
            <FieldError message={message('password')} name="password" />
          </div>
          <div className="hp" aria-hidden="true">
            <label>
              Website
              <input name="website" tabIndex={-1} autoComplete="off" />
            </label>
          </div>
          <div className="field full consent-field">
            <label htmlFor="trialConsent">
              <input
                aria-describedby={message('consent') ? 'consent-error' : undefined}
                aria-invalid={message('consent') ? true : undefined}
                id="trialConsent"
                name="consent"
                required
                type="checkbox"
              />
              <span>
                I agree to the <Link to="/terms">terms</Link> and the{' '}
                <Link to="/privacy">privacy policy</Link>.
              </span>
            </label>
            <FieldError message={message('consent')} name="consent" />
          </div>
        </div>
      </fieldset>

      {step === 'code' && (
        <div className="trial-code">
          <h2 className="card-title">Enter your code</h2>
          <p>
            We sent a 6-digit code by SMS to <strong>{sentTo}</strong>. It can take a minute to
            arrive.
          </p>
          <div className="field">
            <label htmlFor="code">Verification code</label>
            <input
              autoComplete="one-time-code"
              id="code"
              inputMode="numeric"
              maxLength={6}
              name="code"
              pattern="[0-9]{6}"
              ref={codeInput}
              required
            />
          </div>
          <button
            type="button"
            className="link-button"
            disabled={busy}
            onClick={() => void resetVerification()}
          >
            Change the number or send a new code
          </button>
        </div>
      )}

      <div
        aria-live={status.state === 'error' ? 'assertive' : 'polite'}
        className="form-status"
        data-busy={busy ? 'true' : undefined}
        data-state={status.state}
        ref={statusBox}
        role="status"
      >
        {status.text}
      </div>
      <button className="button primary" type="submit" disabled={busy}>
        {submitLabel}
      </button>
      <div id="trial-recaptcha" />
      <p className="trial-note">
        {pricing.freeTrial}. No card and no approval needed: your organisation is ready as soon as
        you finish.
      </p>
      <p className="trial-note">
        Already have an account? <Link to="/reset-password">Reset a forgotten password</Link>.
      </p>
    </form>
  );
}

function FieldError({ name, message }: { name: string; message?: string }) {
  if (!message) return null;
  return (
    <small className="field-error" id={`${name}-error`}>
      {message}
    </small>
  );
}

function Field({
  label,
  name,
  type = 'text',
  required = false,
  optional = false,
  full = false,
  hint,
  error,
  ...input
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  optional?: boolean;
  full?: boolean;
  hint?: string;
  error?: string;
  autoCapitalize?: string;
  autoComplete?: string;
  maxLength?: number;
  pattern?: string;
  placeholder?: string;
  spellCheck?: boolean;
}) {
  const hintId = hint ? `${name}-hint` : undefined;
  return (
    <div className={full ? 'field full' : 'field'}>
      <label htmlFor={name}>
        {label}
        {optional ? <span className="field-optional"> Optional</span> : null}
      </label>
      <input
        aria-describedby={error ? `${name}-error` : hintId}
        aria-invalid={error ? true : undefined}
        id={name}
        maxLength={120}
        name={name}
        required={required}
        type={type}
        {...input}
      />
      {hint && !error ? <small id={hintId}>{hint}</small> : null}
      <FieldError message={error} name={name} />
    </div>
  );
}
